export interface AutomationRuleDto {
  id: string;
  name: string;
  description?: string;
  projectId?: string;
  triggerType: number;
  triggerTypeLabel?: string;
  conditionsJson?: string;
  actionsJson?: string;
  isEnabled: boolean;
  sortOrder?: number;
  createdAt?: string;
}

export interface CreateAutomationRuleRequest {
  name: string;
  description?: string;
  projectId?: string;
  triggerType: number;
  conditionsJson?: string;
  actionsJson: string;
  isEnabled?: boolean;
}

export interface AutomationConditionRow {
  field: string;
  op: string;
  value: string;
}

export const AUTOMATION_CONDITION_OPS = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'not equals' },
  { value: 'contains', label: 'contains' },
  { value: 'gt', label: 'greater than' },
  { value: 'lt', label: 'less than' }
];

export const AUTOMATION_CONDITION_FIELDS = [
  { value: 'status', label: 'Status' },
  { value: 'priority', label: 'Priority' },
  { value: 'type', label: 'Issue type' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'project', label: 'Project' },
  { value: 'sprint', label: 'Sprint' },
  { value: 'epic', label: 'Epic' },
  { value: 'storypoints', label: 'Story points' },
  { value: 'labels', label: 'Labels' }
];

/** Matches backend AutomationTriggerType exactly — no remapping. */
export const AUTOMATION_TRIGGER_OPTIONS = [
  { value: 0, label: 'Issue created' },
  { value: 1, label: 'Issue updated' },
  { value: 2, label: 'Issue assigned' },
  { value: 3, label: 'Issue commented' },
  { value: 4, label: 'Deadline tomorrow' },
  { value: 5, label: 'Deadline passed' },
  { value: 6, label: 'QA failed' },
  { value: 7, label: 'Sprint completed' },
  { value: 8, label: 'Release created' },
  { value: 9, label: 'Status changed' },
  { value: 10, label: 'Attachment uploaded' },
  { value: 11, label: 'Sprint started' },
  { value: 12, label: 'Bug created' }
];

export const AUTOMATION_ACTION_OPTIONS = [
  { value: 'AssignEmployee', label: 'Assign employee' },
  { value: 'NotifyUser', label: 'Notify user' },
  { value: 'NotifyRole', label: 'Notify role' },
  { value: 'SetField', label: 'Set field' },
  { value: 'ChangeStatus', label: 'Change status' },
  { value: 'MoveSprint', label: 'Move sprint' },
  { value: 'SendEmail', label: 'Send email' },
  { value: 'Webhook', label: 'Webhook' },
  { value: 'ApprovalRequest', label: 'Approval request' },
  { value: 'ScheduleReminder', label: 'Schedule reminder' },
  { value: 'AddComment', label: 'Add comment' },
  { value: 'CreateBug', label: 'Create bug' },
  { value: 'CreateTask', label: 'Create task' },
  { value: 'GenerateSprintReportNotify', label: 'Generate sprint report notify' }
];

export interface AutomationTriggerOptionDto {
  value: number;
  label: string;
}

export interface AutomationActionOptionDto {
  value: string;
  label: string;
  configKeys?: string[];
}

export interface AutomationConditionFieldDto {
  value: string;
  label: string;
}

export interface AutomationConditionOperatorDto {
  value: string;
  label: string;
}

export interface AutomationMetadataDto {
  triggers: AutomationTriggerOptionDto[];
  actions: AutomationActionOptionDto[];
  conditionFields: AutomationConditionFieldDto[];
  conditionOperators: AutomationConditionOperatorDto[];
}

export interface PmAutomationExecutionLogDto {
  id: string;
  ruleId: string;
  ruleName: string;
  projectId?: string;
  issueId?: string;
  triggerType: number;
  triggerTypeLabel: string;
  actionType?: string;
  success: boolean;
  message?: string;
  occurredAt: string;
}

/** Pass-through — UI values already match backend AutomationTriggerType. */
export function resolveBackendTriggerType(uiTrigger: number): number {
  return uiTrigger;
}

export function defaultConditionForTrigger(uiTrigger: number): AutomationConditionRow | null {
  switch (uiTrigger) {
    case 9:
      return { field: 'status', op: 'eq', value: '' };
    case 10:
      return { field: 'attachment', op: 'contains', value: '' };
    case 12:
      return { field: 'type', op: 'eq', value: 'Bug' };
    default:
      return null;
  }
}
